import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Building2, Phone, ArrowRight, Sparkles } from 'lucide-react';
import { Toaster, toast } from 'sonner';

const FLOWLEDGER_ICON = "https://customer-assets.emergentagent.com/job_flowsecure-fintech/artifacts/jw6e8f7s_7B7D7191-078E-4AB2-A5E6-D847E0D60608.png";

export const MerchantSetup = () => {
  const navigate = useNavigate();
  const { setupMerchant, user, hasMerchant } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    businessName: '',
    phone: ''
  });

  // Redirect if already has merchant
  if (hasMerchant) {
    navigate('/app/dashboard', { replace: true });
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await setupMerchant(formData.businessName, formData.phone);
      toast.success('Business setup complete!');
      navigate('/app/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Setup failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-sky-50 flex items-center justify-center p-8" data-testid="merchant-setup-page">
      <Toaster richColors position="top-right" />
      
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-6">
            <img src={FLOWLEDGER_ICON} alt="FlowLedger" className="h-14 w-14 object-contain" />
            <span className="text-[#0A1628] font-bold text-2xl">FlowLedger</span>
          </div>
          <div className="inline-flex items-center gap-2 bg-sky-50 text-[#0077B6] px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            Almost there!
          </div>
          <h1 className="text-3xl font-bold text-[#0A1628] mb-2">Set Up Your Business</h1>
          <p className="text-slate-500">
            Hi {user?.name}, let's get your business ready to track transactions
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="businessName">Business Name *</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="businessName"
                  type="text"
                  value={formData.businessName}
                  onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                  className="pl-10 h-12"
                  placeholder="Your Business Name"
                  required
                  data-testid="setup-business-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number (Optional)</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="pl-10 h-12"
                  placeholder="+231 770 000 000"
                  data-testid="setup-phone-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 btn-accent"
              disabled={isLoading || !formData.businessName}
              data-testid="setup-submit-btn"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Complete Setup
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MerchantSetup;
